import { useEffect } from "react";

/**
 * Pezzi condivisi dalle pagine marketing pubbliche: la landing ("/") e la
 * pagina informativa per le campagne social ("/info").
 */

const METRICOOL_PATHS = new Set(["/", "/info"]);
const METRICOOL_CONSENT_MAX_AGE_MS = 180 * 24 * 60 * 60 * 1000;

// La shell PWA blocca lo scroll (html/body/#root/.App overflow:hidden).
// Le pagine marketing sono documenti: scroll normale mentre sono montate.
export function useLandingDocumentScroll() {
	useEffect(() => {
		const els = [
			document.documentElement,
			document.body,
			document.getElementById("root"),
		].filter(Boolean);
		els.forEach((el) => el.classList.add("landing-scroll"));
		return () => els.forEach((el) => el.classList.remove("landing-scroll"));
	}, []);
}

// Tracker Metricool, solo con consenso analytics valido (max 180 giorni).
export function useMetricoolTracker() {
	useEffect(() => {
		const loadMetricool = () => {
			if (
				!METRICOOL_PATHS.has(window.location.pathname) ||
				window.__metricoolTrackerLoaded
			) {
				return;
			}

			let consent;
			try {
				consent = JSON.parse(localStorage.getItem("cookie_consent") || "null");
			} catch (error) {
				return;
			}

			const consentAge = Date.now() - new Date(consent?.timestamp).getTime();
			if (
				!consent?.preferences?.analytics ||
				!Number.isFinite(consentAge) ||
				consentAge < 0 ||
				consentAge > METRICOOL_CONSENT_MAX_AGE_MS
			) {
				return;
			}

			const scriptId = "metricool-tracker";
			if (document.getElementById(scriptId)) return;

			const script = document.createElement("script");
			let initialized = false;
			const initializeTracker = () => {
				if (initialized) return;
				initialized = true;
				window.beTracker?.t({ hash: "561d6e9bae572f4a04179cccc0f239de" });
				window.__metricoolTrackerLoaded = true;
			};

			script.id = scriptId;
			script.type = "text/javascript";
			script.src = "https://tracker.metricool.com/resources/be.js";
			script.async = true;
			script.onload = initializeTracker;
			script.onreadystatechange = initializeTracker;
			script.onerror = () => script.remove();
			document.head.appendChild(script);
		};

		loadMetricool();
		window.addEventListener("cookie-consent-changed", loadMetricool);
		return () =>
			window.removeEventListener("cookie-consent-changed", loadMetricool);
	}, []);
}

// Reveal on scroll degli elementi [data-reveal]. Riparte quando `enabled`
// diventa vero (la landing resta nascosta durante il controllo sessione) e
// quando cambia `resetKey` (la lingua: le card con key testuale si rimontano).
export function useRevealOnScroll(enabled = true, resetKey = null) {
	useEffect(() => {
		if (!enabled) return undefined;
		const targets = Array.from(document.querySelectorAll("[data-reveal]"));
		const reduce = window.matchMedia?.(
			"(prefers-reduced-motion: reduce)",
		)?.matches;
		if (reduce || typeof IntersectionObserver === "undefined") {
			targets.forEach((el) => el.classList.add("is-visible"));
			return undefined;
		}
		const reveal = new IntersectionObserver(
			(entries) => {
				entries.forEach((e) => {
					if (e.isIntersecting) {
						e.target.classList.add("is-visible");
						reveal.unobserve(e.target);
					}
				});
			},
			{ rootMargin: "0px 0px -8% 0px", threshold: 0.08 },
		);
		targets.forEach((el) => reveal.observe(el));
		return () => reveal.disconnect();
	}, [enabled, resetKey]);
}

export function SectionHeader({ label, title, subtitle, align = "center" }) {
	const alignment =
		align === "left" ? "text-left" : "text-center mx-auto max-w-2xl";
	return (
		<div className={alignment} data-reveal>
			<span className="krv-kicker">{label}</span>
			<h2 className="mt-4 font-heading font-extrabold uppercase tracking-tight text-3xl md:text-5xl leading-[1.02]">
				{title}
			</h2>
			{subtitle && (
				<p className="mt-4 text-white/70 text-lg leading-relaxed">{subtitle}</p>
			)}
		</div>
	);
}
